import React, { useState, useCallback } from 'react';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { ArmoryParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import { armoryService } from '../../../services/central-api/armory';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import Filter from './Filters';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import DeleteArmoryShiftModal from './DeleteArmoryShiftModal';
import Toast from 'react-native-toast-message';
import ReplaceInChargeModal from '../ArmoryShifts/ReplaceInChargeModal';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';

export default function ArmoryShifts() {
    const { styles } = useStyles(styleSheet);
    const route = useRoute<RouteProp<ArmoryParamList, 'ArmoryShifts'>>();
    const navigation = useNavigation<NavigationProp<ArmoryParamList>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<ArmoryParamList['ArmoryShifts']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const [selectedArmoryShift, setSelectedArmoryShift] = useState<ArmoryShift>();
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [armoryShifts, setArmoryShifts] = useState<ArmoryShift[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [replaceInChargeModalVisible, setReplaceInChargeModalVisible] = useState<boolean>(false);

    async function getArmoryShifts(): Promise<void> {
        try {
            setIsLoading(true);
            const response = await armoryService.getArmoryShifts(filters);
            setArmoryShifts(response.rows);
            setTotalItems(response.count);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getArmoryShifts();
        }
    }, [isFocused, filters]));

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    const renderStatus = (armoryShift: ArmoryShift) => {
        let customName = 'active';
        let backgroundColor = '#CCE1CF';
        let textColor = '#131513ff';

        if (!armoryShift.definitiveStart && !armoryShift.definitiveFinish && moment(moment().utc()).isBefore(armoryShift.estimatedStart)) {
            customName = 'scheduled';
            backgroundColor = '#DBD3E5';
            textColor = '#573286';
        } else if (!armoryShift.definitiveStart && !armoryShift.definitiveFinish && moment(moment().utc()).isAfter(armoryShift.estimatedFinish)) {
            customName = 'expired';
            backgroundColor = '#D0E4F7';
            textColor = '#1F4E79';
        } else if (!armoryShift.definitiveStart && !armoryShift.definitiveFinish && moment(moment().utc()).isAfter(armoryShift.estimatedStart)) {
            customName = 'overdue';
            backgroundColor = '#E5D1A7';
            textColor = '#744510';
        } else if (armoryShift.definitiveStart && !armoryShift.definitiveFinish && moment(moment().utc()).isAfter(armoryShift.estimatedFinish)) {
            customName = 'exceeded';
            backgroundColor = '#EDC1C1';
            textColor = '#764040';
        } else if (armoryShift.definitiveFinish) {
            customName = 'finished';
            backgroundColor = '#D6D6D6';
            textColor = '#515151';
        }

        return <View style={styles.status(backgroundColor)}>
            <MyAppText style={{ color: textColor }}>{translate(customName)}</MyAppText>
        </View>;
    };

    async function openHandoverReportUrl(armoryShiftId: number) {
        try {
            const res = await armoryService.getHandoverReportUrl(armoryShiftId);
            window.open(res.resultUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <Container>
        <Header
            title={translate('manageShifts')}
            actions={<CustomButton
                text={translate('registerShift')}
                onPress={() => {
                    setSelectedArmoryShift(undefined);
                    navigation.navigate('ArmoryShiftForm', {});
                }}
            />}
        />
        <Card>
            <DataTableHeader
                title={translate('shiftsOfRegisteredArmourers')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('status'),
                        cell: row => renderStatus(row),
                    },
                    {
                        name: translate('armourer'),
                        selector: row => row.user.warName || row.user.name,
                        wrap: true,
                    },
                    {
                        name: translate('actingBody'),
                        selector: row => row.actingBodyUnit?.actingBody?.name ?? '',
                        wrap: true,
                    },
                    {
                        name: translate('unit'),
                        selector: row => row.actingBodyUnit.name,
                        wrap: true,
                    },
                    {
                        name: translate('estimatedDateStart'),
                        selector: row => moment(row.estimatedStart).format('DD/MM/YYYY HH:mm'),
                        wrap: true,
                    },
                    {
                        name: translate('estimatedDateFinish'),
                        selector: row => moment(row.estimatedFinish).format('DD/MM/YYYY HH:mm'),
                        wrap: true,
                    },
                    {
                        name: '',
                        cell: row => {
                            const actions: Action[] = [
                                {
                                    content: row.definitiveFinish ? translate('view') : translate('editInformation'),
                                    onPress: () => {
                                        setSelectedArmoryShift(row);
                                        navigation.navigate('ArmoryShiftForm', { id: row.id });
                                    }
                                }
                            ];
                            if (!row.definitiveStart) {
                                actions.push({
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedArmoryShift(row);
                                        setDeleteModalVisible(true);
                                    },
                                });
                            }

                            if (row.handoverReportUrl) {
                                actions.push({
                                    content: translate('downloadHandoverReport'),
                                    onPress: () => {
                                        openHandoverReportUrl(row.id);
                                    },
                                });
                            }

                            if (row.definitiveStart && !row.definitiveFinish && authenticatedUser?.permissions.armory_replace_in_charge_armourer) {
                                actions.push({
                                    content: translate('emergencyReplace'),
                                    onPress: () => {
                                        setSelectedArmoryShift(row);
                                        setReplaceInChargeModalVisible(true);
                                    },
                                });
                            }
                            return (
                                <DropdownButton actions={actions} />
                            );
                        },
                        wrap: true,
                        button: true
                    }]}
                    data={armoryShifts}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    setPage={page => {
                        setFilters(old => ({ ...old, page }));
                        navigation.setParams({ ...route.params, page: page + 1 });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                        navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                    }}
                />}
            />
        </Card>

        {selectedArmoryShift &&
            <DeleteArmoryShiftModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={deleteModalVisible}
                armoryShiftId={selectedArmoryShift.id}
                onClose={() => {
                    setSelectedArmoryShift(undefined);
                    setDeleteModalVisible(false);
                    getArmoryShifts();
                }}
                onSave={() => {
                    setSelectedArmoryShift(undefined);
                    setDeleteModalVisible(false);
                    getArmoryShifts();
                }}
            />}

        {selectedArmoryShift &&
            <ReplaceInChargeModal
                armoryShiftId={selectedArmoryShift.id}
                isModalVisible={replaceInChargeModalVisible}
                setModalVisible={setReplaceInChargeModalVisible}
                onSubmit={() => {
                    setSelectedArmoryShift(undefined);
                    getArmoryShifts();
                }}
            />}
    </Container>;

}

const styleSheet = createStyleSheet(() => ({
    status: (backgroundColor: string) => ({
        backgroundColor,
        borderRadius: 4,
        paddingVertical: 2,
        paddingHorizontal: 8,
    }),
}));
